import React, { useEffect, useState, useRef } from "react";
import { Spinner, Table } from "react-bootstrap";
import { GetSharedWithMeFilesAsync } from "../../../../../services/EmployeeService/EmployeeService";
import {
  SharedFileDto,
} from "../../../../../interfaces/Employee/Employee";
import { useAuth } from "../../../../../firebase/AuthProvider";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FileText } from "lucide-react";
import { useDriveContext } from "../../../../../contexts/DriveContext";
import { convertUtcToLocalDate } from "../../../../../utilities/dateUtils";
import NoParticipantsFound from "../../../participantfiles/noParticipantsFound";
import "../../../participantfiles/noParticipantsFound.css";
import "./SharedFilesDrive.css";
import { getTeamBySupervisor } from "../../../../../services/userServices/TeamService";
import { getUserInfoByUserId } from "../../../../../services/userServices/UserInfoService";
import UserInfo from "../../../../../interfaces/UserInfo";
import { TeamUser } from "../../../../../interfaces/team/TeamUser";
import AvatarImage from "../../../../ui/Avatar/AvatarImage";
import "../../../participantfiles/participantfiles.css";

const SharedFilesDrive = () => {
  const { currentUser, currentRoles } = useAuth();
  const navigate = useNavigate();
  const { searchTerm, pagination, setPagination, setTotalItems } = useDriveContext();
  const [authToken, setAuthToken] = useState<string>();

  // Carousel state
  const carouselRef = useRef<HTMLDivElement>(null);
  const [employees, setEmployees] = useState<TeamUser[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(true);

  // data
  const [driveFile, setDriveFile] = useState<SharedFileDto[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [refreshList, setRefreshList] = useState<boolean>(true);

  // loading state
  const [loading, setLoading] = useState<boolean>(true);

  const [sortBy, setSortBy] = useState<string>("uploadedOn");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const hasHigherRoleThanSC = currentRoles?.some(
    (role) =>
      role.name === "SUPERVISOR" ||
      role.name === "OWNER" ||
      role.name === "ADMIN" ||
      role.name === "PROGRAM MANAGER"
  );

  // Function to abbreviate role names
  const getRoleAbbreviation = (role: string | undefined | null): string => {
    if (!role) return '';
    const roleUpper = role.toUpperCase().trim();
    if (roleUpper === 'PROGRAM MANAGER') return 'PM';
    if (roleUpper === 'SUPERVISOR') return 'SUP';
    return role;
  };

  // Get highest role for a user
  const getHighestRole = (roles: any[] | undefined) => {
    if (!Array.isArray(roles) || roles.length === 0) {
      return null;
    }

    const rolePriority = [
      "ADMIN",
      "OWNER",
      "PROGRAM MANAGER",
      "SUPERVISOR",
      "SC",
      "ASSESSOR",
    ];

    const roleLabelMap: any = {
      ADMIN: "Admin",
      OWNER: "Owner",
      SUPERVISOR: "Supervisor",
      "PROGRAM MANAGER": "Program manager",
      SC: "Support coordinator",
      ASSESSOR: "Assessor",
    };

    const normalizedRoles = roles
      .filter(Boolean)
      .map((role) => (typeof role === "string" ? role : role.name || "").toUpperCase());

    const matchedRole = rolePriority.find((role) =>
      normalizedRoles.includes(role)
    );

    if (matchedRole) {
      return roleLabelMap[matchedRole];
    }

    const fallbackRole = roles[0];
    if (typeof fallbackRole === "string") {
      return fallbackRole
        .toLowerCase()
        .replace(/\b\w/g, (char: string) => char.toUpperCase());
    }
    return fallbackRole?.name || "";
  };

  useEffect(() => {
    currentUser?.getIdToken().then((token) => {
      setAuthToken(token);
    });
  }, []);

  // Fetch employees if supervisor
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!currentUser || !hasHigherRoleThanSC) return;
      try {
        const token = await currentUser.getIdToken();
        const employeesData = await getTeamBySupervisor(token);
        if (employeesData) {
          setEmployees(employeesData);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    fetchEmployees();
  }, [currentUser, hasHigherRoleThanSC]);

  // Check scroll position on mount and when employees changes
  useEffect(() => {
    if (hasHigherRoleThanSC && employees && employees.length > 0) {
      setTimeout(() => {
        if (carouselRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
          setShowLeftArrow(scrollLeft > 0);
          setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
        }
      }, 100);
    }
  }, [employees, hasHigherRoleThanSC]);

  // Debounce searchTerm changes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!currentUser || !authToken) return;

    // Use selectedEmployeeId if available, otherwise use currentUser.uid
    const userId = selectedEmployeeId || currentUser.uid;

    GetSharedWithMeFiles(
      userId,
      authToken,
      pagination.pageNumber,
      pagination.pageSize,
      debouncedSearchTerm,
      sortBy,
      sortDirection
    );
  }, [
    authToken,
    pagination,
    debouncedSearchTerm,
    refreshList,
    sortBy,
    sortDirection,
    selectedEmployeeId,
    currentUser,
  ]);

  const GetSharedWithMeFiles = (
    userId: string,
    token: string,
    pageNum: number,
    pageSize: number,
    searchText: string,
    sortBy?: string,
    sortDirection?: "asc" | "desc"
  ) => {
    setLoading(true);
    GetSharedWithMeFilesAsync(
      token,
      userId,
      pageNum,
      pageSize,
      searchText,
      sortBy,
      sortDirection
    )
      .then((res) => {
        if (res.success) {
          setDriveFile(res.data.sharedFileDto);
          setTotalItems(res.data.count);
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };


  function handleItemClick(file: SharedFileDto) {
    if (file.fileType == "casefile") {
      navigate(`/casefile/file/${file.fileId}`);
    } else {
      navigate(`/view/user/file/${file.fileId}`);
    }
  }


  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortBy !== column) {
      return <span className="text-[#6A7282]">⇅</span>;
    }
    return sortDirection === "asc" ? (
      <span className="text-[#6A7282]">▲</span>
    ) : (
      <span className="text-[#6A7282]">▼</span>
    );
  };

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    if (!carouselRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Scroll carousel left
  const scrollLeft = () => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.clientWidth * 0.8;
      carouselRef.current.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Scroll carousel right
  const scrollRight = () => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.clientWidth * 0.8;
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Handle carousel card click - fetch files for that employee
  const handleCarouselCardClick = async (empId: string) => {
    if (!currentUser) return;

    // If clicking the same card, reset to viewing logged-in user's files
    if (selectedEmployeeId === empId) {
      setSelectedEmployeeId(null);
      setPagination((prev) => ({ ...prev, pageNumber: 1 }));
      return;
    }

    setLoading(true);
    try {
      const authToken = await currentUser.getIdToken();

      // Fetch user info for the clicked employee
      const userInfoData = await getUserInfoByUserId(empId, authToken);
      if (userInfoData) {
        setUserInfo([userInfoData]);
      }

      // Set selected employee ID to fetch their files
      setSelectedEmployeeId(empId);
      
      // Reset pagination when switching employees
      setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    } catch (error) {
      console.error("Failed to fetch employee data:", error);
      toast.error("Failed to load employee files");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner size="sm" /> Loading...
      </div>
    );
  }

  return (
    <>
      {/* Section for My Team Carousel (if supervisor) */}
      {hasHigherRoleThanSC && employees && employees.length > 0 && (
        <div className="common_content_inner mby_css">
          <div className="team_carousel_wrapper">
            {showLeftArrow && (
              <button
                className="team_carousel_arrow team_carousel_arrow_left"
                onClick={scrollLeft}
                aria-label="Scroll left"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="7"
                  height="12"
                  viewBox="0 0 7 12"
                  fill="none"
                  style={{ transform: "rotate(180deg)" }}
                >
                  <path
                    d="M0.699375 0L6.35938 5.66L0.699375 11.32L-0.00062561 10.61L4.94938 5.66L-0.00062561 0.71L0.699375 0Z"
                    fill="black"
                  />
                </svg>
              </button>
            )}
            <div
              className="team_carousel_container"
              ref={carouselRef}
              onScroll={checkScrollPosition}
            >
              {employees.map((emp, index) => {
                // Find userInfo for this employee if it's been fetched
                const userInfoForEmp = userInfo.find(u => u.id === emp.id);
                const highestRole = userInfoForEmp ? getHighestRole(userInfoForEmp.roles) : null;
                const isSelected = selectedEmployeeId === emp.id;
                return (
                  <div
                    key={emp.id || index}
                    className={`team_member_card ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      e.preventDefault(); 
                      handleCarouselCardClick(emp.id);
                    }}
                  >
                    <div className="team_member_content">
                      <div className="team_member_avatar">
                        <AvatarImage userId={emp.id} />
                      </div>
                      <div className="team_member_info">
                        <div className="team_member_name">
                          {emp.firstName} {emp.lastName}
                        </div>
                        <div className="roleParticipantCountContainer">
                          <span className="ParticipantCount participantrolecounttext">
                            <svg className="mb-[1px]" xmlns="http://www.w3.org/2000/svg" width="16" height="11" viewBox="0 0 18 13" fill="none">
                              <path d="M12 8.18164C13.2222 8.18166 14.3431 8.4576 15.2617 8.9668L15.4424 9.07129C16.5021 9.71303 17.1998 10.6475 17.4805 11.7764L17.4814 11.7793C17.5042 11.8685 17.5062 11.9617 17.4873 12.0518C17.4685 12.1416 17.4283 12.226 17.3711 12.2998C17.3215 12.361 17.2585 12.4115 17.1855 12.4463C17.1112 12.4817 17.0295 12.5004 16.9463 12.5H7.05371C6.97021 12.5004 6.88803 12.4819 6.81348 12.4463C6.77601 12.4284 6.74084 12.4063 6.70898 12.3809L6.62402 12.2959C6.56836 12.2229 6.53005 12.1392 6.51172 12.0508C6.49309 11.9608 6.49553 11.8674 6.51855 11.7783L6.51953 11.7764C6.80116 10.6626 7.4972 9.7324 8.55664 9.08594C9.50226 8.50924 10.7254 8.18164 12 8.18164ZM4.91309 7.83301C5.74916 7.83304 6.47953 7.96608 7.03809 8.20703C6.01149 9.01316 5.29976 10.0763 4.9707 11.3184H0.961914C0.892031 11.3186 0.822998 11.3032 0.760742 11.2734C0.69893 11.2438 0.645962 11.2005 0.604492 11.1484C0.557914 11.0877 0.526043 11.0178 0.510742 10.9443C0.495313 10.87 0.496717 10.7933 0.515625 10.7197L0.516602 10.7178C0.742362 9.82488 1.29987 9.0786 2.15234 8.55762C2.90865 8.09631 3.88942 7.83301 4.91309 7.83301ZM4.91211 1.68164C5.53295 1.68164 6.07424 1.91367 6.45898 2.32129V2.32227C6.8475 2.7337 7.04547 3.29489 6.99902 3.91797V3.91895C6.9065 5.19507 5.91743 6.14746 4.91211 6.14746C3.90442 6.14726 2.91573 5.19409 2.82324 3.91797C2.78486 3.36509 2.9335 2.86514 3.22852 2.47656L3.36328 2.31738C3.74392 1.91424 4.28916 1.68172 4.91211 1.68164ZM12 0.5C12.7666 0.500026 13.44 0.785594 13.9131 1.28613C14.3296 1.72696 14.5668 2.31312 14.582 2.9707L14.5742 3.25684C14.5195 3.99989 14.2191 4.69004 13.7354 5.20215C13.2375 5.72877 12.6195 5.99997 12 6C11.3808 6 10.7623 5.72804 10.2627 5.20117C9.83851 4.75215 9.5549 4.16786 9.45508 3.53223L9.42383 3.25684L9.41602 2.96777C9.43122 2.30358 9.66885 1.71706 10.0811 1.28125C10.5545 0.780859 11.2247 0.5 12 0.5Z" stroke="#747474" />
                            </svg>
                            {(emp as any)?.participantCount ?? 0}
                          </span>
                          <span className="role participantrolecounttext">
                            <svg className="mb-[1px]" xmlns="http://www.w3.org/2000/svg" width="12" height="11" viewBox="0 0 12 13" fill="none">
                              <path d="M3.5 10.0996C4.58775 8.92722 6.60004 8.87203 7.69979 10.0996M6.76384 6.49974C6.76384 7.16271 6.24126 7.69968 5.5957 7.69968C4.95073 7.69968 4.42755 7.16271 4.42755 6.49974C4.42755 5.83678 4.95073 5.2998 5.5957 5.2998C6.24066 5.2998 6.76444 5.83678 6.76444 6.49974H6.76384Z" stroke="#747474" strokeLinecap="round" />
                              <path d="M4.09982 1.70117C2.5135 1.70717 1.66454 1.76237 1.11497 2.28314C0.5 2.86571 0.5 3.80347 0.5 5.67897V8.52103C0.5 10.3965 0.5 11.3349 1.11497 11.9169C1.72994 12.5 2.71989 12.5 4.69979 12.5H6.4997C8.4796 12.5 9.46955 12.5 10.0845 11.9175C10.6995 11.3343 10.6995 10.3965 10.6995 8.52163V5.67897C10.6995 3.80347 10.6995 2.86571 10.0845 2.28314C9.53495 1.76237 8.68659 1.70717 7.09967 1.70117" stroke="#747474" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M4.2646 1.47915C4.3222 1.23016 4.35099 1.10537 4.40619 1.00397C4.47481 0.883328 4.56818 0.778565 4.68017 0.696566C4.79216 0.614567 4.92022 0.557188 5.05596 0.528199C5.17775 0.5 5.31935 0.5 5.60133 0.5C5.88332 0.5 6.02491 0.5 6.14671 0.528199C6.28244 0.557188 6.41051 0.614567 6.52249 0.696566C6.63448 0.778565 6.72785 0.883328 6.79647 1.00397C6.85167 1.10597 6.88047 1.23016 6.93807 1.47915L6.98786 1.69514C7.08986 2.13612 7.14085 2.35691 7.06406 2.5267C7.01172 2.63939 6.92662 2.73369 6.81987 2.79729C6.65128 2.89988 6.40049 2.89988 5.90012 2.89988H5.30255C4.80217 2.89988 4.55138 2.89988 4.38279 2.79789C4.27595 2.73413 4.19085 2.63961 4.13861 2.5267C4.06181 2.35691 4.11281 2.13672 4.2148 1.69514L4.2646 1.47915Z" stroke="#747474" />
                            </svg>
                            <span className="participantrolecounttext-text">{getRoleAbbreviation((emp as any)?.role)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {showRightArrow && (
              <button
                className="team_carousel_arrow team_carousel_arrow_right"
                onClick={scrollRight}
                aria-label="Scroll right"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="7"
                  height="12"
                  viewBox="0 0 7 12"
                  fill="none"
                >
                  <path
                    d="M0.699375 0L6.35938 5.66L0.699375 11.32L-0.00062561 10.61L4.94938 5.66L-0.00062561 0.71L0.699375 0Z"
                    fill="black"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      <div className={`common_content_inner mby_t_24 ${pagination.pageSize === 10 ? 'drive-page-size-10' : ''} drive-content-container`}>
        <div className="personal_flx">
          <div className="personal_flx_left">
            {/* List/Table View */}
            <div className="drive-table-wrapper">
              <Table responsive className="drive-table">
              <thead>
                <tr>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("fileName")}
                  >
                    Name {getSortIndicator("fileName")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("uploadedByUserName")}
                  >
                    Employee {getSortIndicator("uploadedByUserName")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("uploadedOn")}
                  >
                    Created {getSortIndicator("uploadedOn")}
                  </th>
                  <th>
                    File Size
                  </th>
                </tr>
              </thead>
              <tbody>
                {driveFile.length === 0 ? (
                  <tr className="no-data-row">
                    <td colSpan={4} style={{ padding: 0, border: "none" }}>
                      <div className="no-participants-found-container">
                        <NoParticipantsFound />
                      </div>
                    </td>
                  </tr>
                ) : (
                  driveFile.map((item, index) => (
                    <tr
                      className="cursor-pointer"
                      key={index}
                      onClick={() => handleItemClick(item)}
                    >
                      <td>
                        <div className="tableicon-tab">
                          <span className="tableicon">
                            <FileText className="tableicon-svg" />
                          </span>
                          <span className="font-normal text-[#354152] text-lg tracking-[0] leading-6 truncate">
                            {item.fileName}
                          </span>
                        </div>
                      </td>
                      <td>{item.uploadedByUserName}</td>
                      <td>
                        {item.uploadedOn
                          ? (() => {
                              const localDate = convertUtcToLocalDate(item.uploadedOn);
                              return localDate ? localDate.toLocaleDateString() : "N/A";
                            })()
                          : "N/A"}
                      </td>
                      <td>22MB</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SharedFilesDrive;
